import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Mic, ShoppingCart, X, Loader2, CheckCircle2, Banknote, ArrowRight, Sparkles, Trash2 } from 'lucide-react';

interface Product {
  id: number;
  name: string;
  price: number;
  unitType?: 'Pieces' | 'Kg' | 'Liters' | 'Grams' | 'ml';
  stockCount: number;
}

interface CartLine {
  id: string;
  product: Product;
  qty: number;
  unitPrice: number;
  total: number;
}

interface VoiceCheckoutModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
  inventory: Product[];
  onCheckoutComplete: () => void;
}

export default function VoiceCheckoutModal({ isOpen, onClose, storeApiKey, inventory, onCheckoutComplete }: VoiceCheckoutModalProps) {
  const [cartLines, setCartLines] = useState<CartLine[]>([]);
  const [cashTendered, setCashTendered] = useState<string>('');
  const [listening, setListening] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [justAdded, setJustAdded] = useState<Set<string>>(new Set());
  const inputRef = useRef<HTMLInputElement>(null);

  // Reset on open
  useEffect(() => {
    if (isOpen) {
      setCartLines([]);
      setCashTendered('');
      setListening(true);
      setProcessing(false);
      setCompleted(false);
      setJustAdded(new Set());
    }
  }, [isOpen]);

  // Listen for unified express checkout events from Luna
  useEffect(() => {
    if (!isOpen) return;

    const syncHandler = (e: Event) => {
      const detail = (e as CustomEvent).detail || {};

      if (detail.action === 'ADD_ITEMS' && detail.items) {
        const newIds: string[] = [];

        detail.items.forEach((aiItem: any) => {
          const aiName = aiItem.name || aiItem.itemName || '';
          const qty = parseFloat(aiItem.qty || aiItem.quantity || 1);
          if (!aiName) return;

          // Safely find the product in inventory (case insensitive, partial match)
          const matchedProduct = inventory.find(p =>
            p.name.toLowerCase().includes(aiName.toLowerCase()) ||
            aiName.toLowerCase().includes(p.name.toLowerCase())
          );

          const price = matchedProduct ? matchedProduct.price : 0; // Fallback to 0 if not found so UI doesn't crash
          const actualName = matchedProduct ? matchedProduct.name : aiName;

          // Check if already in cart to update qty, else add new
          setCartLines(prev => {
            const existingIndex = prev.findIndex(i => i.product.name === actualName);
            if (existingIndex >= 0) {
              const updated = [...prev];
              const existing = updated[existingIndex];
              const newQty = existing.qty + qty;
              updated[existingIndex] = {
                ...existing,
                qty: newQty,
                total: newQty * existing.unitPrice
              };
              return updated;
            } else {
              const id = `line_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
              newIds.push(id);
              return [...prev, {
                id,
                product: matchedProduct || { id: 0, name: actualName, price: 0, stockCount: 0 },
                qty,
                unitPrice: price,
                total: qty * price
              }];
            }
          });
        });

        if (newIds.length > 0) {
          setJustAdded(prev => new Set([...prev, ...newIds]));
          setTimeout(() => {
            setJustAdded(p => { const n = new Set(p); newIds.forEach(id => n.delete(id)); return n; });
          }, 1200);
        }
      } else if (detail.action === 'SET_CASH_TENDERED') {
        const amount = detail.amount ?? 0;
        if (typeof amount === 'number' || typeof amount === 'string') {
          setCashTendered(String(amount));
        }
      } else if (detail.action === 'COMPLETE_BILL') {
        doCompleteCheckout();
      }
    };

    window.addEventListener('luna-express-sync', syncHandler);

    return () => {
      window.removeEventListener('luna-express-sync', syncHandler);
    };
  }, [isOpen, inventory]);

  const grandTotal = cartLines.reduce((sum, line) => sum + line.total, 0);
  const parsedCash = parseFloat(cashTendered) || 0;
  const changeDue = Math.max(0, parsedCash - grandTotal);
  const isValid = grandTotal > 0 && parsedCash >= grandTotal;

  const removeLine = (id: string) => {
    setCartLines(prev => prev.filter(l => l.id !== id));
  };

  const updateQty = (id: string, delta: number) => {
    setCartLines(prev => prev.map(line => {
      if (line.id !== id) return line;
      const newQty = Math.max(0.01, line.qty + delta);
      return { ...line, qty: newQty, total: newQty * line.unitPrice };
    }));
  };

  const doCompleteCheckout = async () => {
    if (!isValid || processing) return;
    setProcessing(true);

    try {
      const txRef = `VOICE-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      const token = localStorage.getItem('kangavena:cashierToken') || '';

      const payload = {
        apiKey: storeApiKey,
        txRef,
        cart: cartLines.map(line => ({
          productId: line.product.id,
          qty: line.qty,
        })),
        subtotal: grandTotal,
        discountPct: 0,
        discountAmt: 0,
        total: grandTotal,
        paymentMethod: 'CASH',
        cashTendered: grandTotal,
      };

      const res = await fetch('/api/pos-checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Checkout failed (HTTP ${res.status})`);
      }

      setCompleted(true);
      setTimeout(() => {
        onCheckoutComplete();
        onClose();
      }, 2000);
    } catch (err) {
      console.error('Checkout error:', err);
    } finally {
      setProcessing(false);
    }
  };

  const rowGlow = (id: string) => justAdded.has(id)
    ? 'bg-cyan-500/10 border-cyan-400/30 shadow-[0_0_15px_rgba(6,182,212,0.25)]'
    : 'bg-white/[0.02] border-white/[0.04]';

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      style={{ background: 'radial-gradient(ellipse at center, rgba(245,158,11,0.08) 0%, rgba(0,0,0,0.9) 70%)' }}
      onClick={e => { if (e.target === e.currentTarget && !processing) onClose(); }}
    >
      {/* Scanlines */}
      <div className="pointer-events-none fixed inset-0 z-[1]"
        style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(245,158,11,0.02) 2px, rgba(245,158,11,0.02) 4px)',
        }} />

      <motion.div
        initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.9, y: 20, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 25 }}
        className="relative z-10 w-full max-w-3xl max-h-[90vh] rounded-3xl overflow-hidden flex flex-col"
        style={{
          background: 'linear-gradient(135deg, rgba(245,158,11,0.06) 0%, rgba(15,20,40,0.98) 40%, rgba(10,14,26,0.99) 100%)',
          border: '1px solid rgba(245,158,11,0.25)',
          boxShadow: '0 0 100px rgba(245,158,11,0.12), 0 0 300px rgba(245,158,11,0.05), inset 0 1px 0 rgba(245,158,11,0.15)',
        }}
      >
        {/* Top shimmer */}
        <div className="absolute top-0 left-0 right-0 h-[2px]"
          style={{
            background: 'linear-gradient(90deg, transparent 0%, rgba(245,158,11,0.9) 30%, rgba(234,179,8,0.7) 70%, transparent 100%)',
            animation: 'shimmer 2.5s ease-in-out infinite',
          }} />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-amber-500/10">
          <div className="flex items-center gap-3">
            <motion.div
              animate={listening ? {
                boxShadow: ['0 0 12px rgba(245,158,11,0.3)', '0 0 28px rgba(245,158,11,0.6)', '0 0 12px rgba(245,158,11,0.3)']
              } : {}}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center"
            >
              {completed ? <CheckCircle2 size={18} className="text-emerald-400" /> :
               processing ? <Loader2 size={18} className="text-amber-400 animate-spin" /> :
               <ShoppingCart size={18} className="text-amber-400" />}
            </motion.div>
            <div>
              <h2 className="text-sm font-black text-white tracking-wide flex items-center gap-2">
                Express Checkout
                <Sparkles size={12} className="text-amber-400" />
              </h2>
              <p className="text-[9px] text-amber-400/60 font-mono">
                {completed ? '✓ TRANSACTION COMPLETE' :
                 processing ? '● PROCESSING...' :
                 listening ? `● VOICE ACTIVE — ${cartLines.length} item(s)` : '○ PAUSED'}
              </p>
            </div>
          </div>

          {/* Voice status badge */}
          {!completed && !processing && (
            <div className="flex items-center gap-2">
              <motion.div
                animate={{ scale: [1, 1.2, 1], opacity: [0.7, 1, 0.7] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-500/30"
              >
                <Mic size={10} className="text-amber-400" />
                <span className="text-[9px] font-bold text-amber-300 uppercase tracking-wider">I'm Ready</span>
              </motion.div>
              <button onClick={onClose} className="p-2 rounded-xl hover:bg-white/8 text-slate-600 hover:text-white transition-colors">
                <X size={16} />
              </button>
            </div>
          )}
        </div>

        {completed ? (
          <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="flex flex-col items-center justify-center py-16 gap-4">
            <motion.div animate={{ scale: [1, 1.2, 1] }} transition={{ duration: 0.6, repeat: 2 }}>
              <CheckCircle2 size={64} className="text-emerald-400" />
            </motion.div>
            <p className="text-2xl font-black text-white">Bill Complete!</p>
            <div className="text-center space-y-1">
              <p className="text-[11px] text-emerald-400/80 font-mono">Change: Rs. {changeDue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}</p>
              <p className="text-[10px] text-slate-500 font-mono">Thank you for shopping</p>
            </div>
          </motion.div>
        ) : (
          <>
            {/* Dictation hint */}
            <div className="px-5 py-2 bg-amber-500/[0.04] border-b border-amber-500/10">
              <p className="text-[10px] text-amber-400/70 font-mono text-center">
                🎙️ <span className="text-amber-300">Dictate items:</span> "Samba rice 2kg" • "Coca cola 3 bottles" • "Sugar 1kg"
              </p>
            </div>

            {/* Cart Table */}
            <div className="flex-1 overflow-auto px-5 py-3">
              <table className="w-full text-[11px]">
                <thead>
                  <tr className="text-[8px] text-amber-400/50 uppercase tracking-[0.15em] font-mono border-b border-amber-500/20">
                    <th className="text-left py-2 px-2 w-[40%]">Item</th>
                    <th className="text-center py-2 px-2 w-[15%]">Qty</th>
                    <th className="text-right py-2 px-2 w-[20%]">Unit</th>
                    <th className="text-right py-2 px-2 w-[20%]">Total</th>
                    <th className="w-[5%]"></th>
                  </tr>
                </thead>
                <tbody>
                  <AnimatePresence>
                    {cartLines.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center">
                          <p className="text-slate-700 font-mono text-[11px]">
                            <Mic size={24} className="inline-block mb-2 opacity-30" /><br />
                            Cart is empty<br />
                            <span className="text-[9px] opacity-50">Start dictating products...</span>
                          </p>
                        </td>
                      </tr>
                    ) : (
                      cartLines.map((line, idx) => (
                        <motion.tr
                          key={line.id}
                          initial={{ opacity: 0, x: -20, scale: 0.98 }}
                          animate={{ opacity: 1, x: 0, scale: 1 }}
                          exit={{ opacity: 0, x: 20 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 25, delay: idx * 0.02 }}
                          className={`border-b border-white/[0.03] ${rowGlow(line.id)} transition-all duration-500`}
                        >
                          <td className="py-2 px-2">
                            <p className="text-white text-[11px] font-bold font-mono truncate">{line.product.name}</p>
                            <p className="text-[9px] text-slate-600">{line.product.category}</p>
                          </td>
                          <td className="py-2 px-2">
                            <div className="flex items-center justify-center gap-1">
                              <button
                                onClick={() => updateQty(line.id, line.product.unitType === 'Pieces' ? -1 : -0.5)}
                                className="w-6 h-6 rounded bg-white/5 hover:bg-white/10 text-slate-500 hover:text-white text-[10px] transition-colors"
                              >
                                −
                              </button>
                              <span className="text-amber-300 text-[11px] font-mono w-12 text-center">
                                {line.qty}
                                {line.product.unitType && line.product.unitType !== 'Pieces' ? line.product.unitType === 'Grams' ? 'g' : line.product.unitType === 'ml' ? 'ml' : line.product.unitType : ''}
                              </span>
                              <button
                                onClick={() => updateQty(line.id, line.product.unitType === 'Pieces' ? 1 : 0.5)}
                                className="w-6 h-6 rounded bg-white/5 hover:bg-white/10 text-slate-500 hover:text-white text-[10px] transition-colors"
                              >
                                +
                              </button>
                            </div>
                          </td>
                          <td className="py-2 px-2 text-right">
                            <p className="text-slate-400 text-[10px] font-mono">
                              Rs. {line.unitPrice.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                            </p>
                          </td>
                          <td className="py-2 px-2 text-right">
                            <p className="text-emerald-300 text-[11px] font-mono font-bold">
                              Rs. {line.total.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                            </p>
                          </td>
                          <td className="py-2 px-1">
                            <button
                              onClick={() => removeLine(line.id)}
                              className="p-1 rounded hover:bg-red-500/10 text-slate-600 hover:text-red-400 transition-colors"
                            >
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </motion.tr>
                      ))
                    )}
                  </AnimatePresence>
                </tbody>
              </table>
            </div>

            {/* Footer — Totals & Checkout */}
            <div className="px-5 py-4 border-t border-amber-500/10 bg-black/20 space-y-3">
              {/* Grand Total */}
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500 uppercase tracking-wider font-mono">Grand Total</span>
                <span className="text-xl font-black text-white font-mono">
                  Rs. {grandTotal.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                </span>
              </div>

              {/* Cash Tendered */}
              <div className="flex items-center gap-3">
                <div className="flex-1 flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.03] border border-white/[0.08] focus-within:border-amber-500/40 transition-colors">
                  <Banknote size={14} className="text-amber-500/50" />
                  <input
                    ref={inputRef}
                    type="number"
                    value={cashTendered}
                    onChange={e => setCashTendered(e.target.value)}
                    placeholder="Cash received..."
                    className="flex-1 bg-transparent text-[13px] text-white placeholder:text-slate-700 outline-none font-mono"
                  />
                </div>
                {changeDue > 0 && (
                  <motion.div
                    initial={{ opacity: 0, x: 10 }} animate={{ opacity: 1, x: 0 }}
                    className="px-3 py-2 rounded-xl bg-emerald-500/10 border border-emerald-500/30"
                  >
                    <span className="text-[10px] text-emerald-400 font-mono">
                      Change: Rs. {changeDue.toLocaleString('en-LK', { minimumFractionDigits: 2 })}
                    </span>
                  </motion.div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => { setCartLines([]); setCashTendered(''); }}
                  disabled={processing || cartLines.length === 0}
                  className="px-4 py-2.5 rounded-xl bg-white/[0.04] border border-white/[0.08] text-slate-500 hover:text-white text-[10px] font-bold transition-all disabled:opacity-30"
                >
                  Clear
                </button>

                <motion.button
                  whileTap={{ scale: 0.97 }}
                  onClick={doCompleteCheckout}
                  disabled={!isValid || processing}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-black text-[11px] font-black uppercase tracking-wider shadow-[0_0_20px_rgba(245,158,11,0.3)] hover:shadow-[0_0_40px_rgba(245,158,11,0.5)] disabled:opacity-30 disabled:cursor-not-allowed transition-all"
                >
                  {processing ? <Loader2 size={14} className="animate-spin" /> : <ArrowRight size={14} />}
                  {processing ? 'Processing...' : 'CONFIRM BILL'}
                </motion.button>
              </div>
            </div>
          </>
        )}
      </motion.div>

      <style>{`
        @keyframes shimmer {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 1; }
        }
      `}</style>
    </motion.div>
  );
}
